import React, {
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import {
	Button,
	Divider,
	Form,
	Icon,
	Label,
	Message,
	Segment
} from 'semantic-ui-react';

import { stateOptions } from '../../../services/constants';
import {
	validateEmail,
	validatePhone,
	validateZip
} from '../../../services/validation';

import useForm from '../../../hooks/useForm';

import {
	getUser,
	updateUser
} from '../../../api/nora/users';

import { useAdminState, useAdminAction } from '../../../contexts/AdminContext';
import { useAuthState } from '../../../contexts/AuthContext';
import { useUIAction } from '../../../contexts/UIContext';

import EmailValidationInfoPopup from '../../Form/EmailValidationInfoPopup';
import FormCheckbox from '../../Form/FormCheckbox';
import FormInput from '../../Form/FormInput';
import FormSelect from '../../Form/FormSelect';
import HelpModal from '../../Shared/HelpModal';
import PermissionsWrapper from '../../PermissionsWrapper';
import PhoneValidationInfoPopup from '../../Form/PhoneValidationInfoPopup';

import styles from '../agency-portal.module.css';

const Admin = () => {
	const {
		user,
		initialized
	} = useAdminState();
	const {
		setUser,
		clearUser
	} = useAdminAction();
	const { user: { id, permissions } } = useAuthState();
	const {
		setApiErrorAlert,
		setInfobarLoader
	} = useUIAction();

	const [ forceValidation, setForceValidation ] = useState( false );
	const [ editable, setEditable ] = useState( false );

	const {
		formData,
		resetFormData,
		setFormData,
		updateField
	} = useForm( {} );

	useEffect( () => {
		if ( !initialized ) {
			setInfobarLoader( 'Please wait...' );
		} else {
			( async () => {
				setInfobarLoader( 'Retrieving User Info...' );

				try {
					const response = await getUser( id );

					setUser( response );
				} catch ( error ) {
					setApiErrorAlert( error );
				}

				setInfobarLoader( '' );
			} )();
		}

		return () => {
			clearUser();
		};
	}, [
		clearUser,
		id,
		initialized,
		setApiErrorAlert,
		setInfobarLoader,
		setUser
	] );

	useEffect( () => {
		if ( user ) {
			setFormData( {
				...user,
				// notification settings
				notificationDigestType: user.notificationSettings.digestType.id,
				...user.notificationSettings.events
					.reduce( ( acc, { id, active } ) => ( { ...acc, [ `event-${ id }` ]: active } ), {} ),
				emailNotifications: user.notificationSettings.email,
				smsNotifications: user.notificationSettings.sms,
				// profile
				address: user.profile.address,
				address2: user.profile.address2,
				city: user.profile.city,
				email: user.profile.email,
				firstName: user.profile.firstName,
				lastName: user.profile.lastName,
				phone: user.profile.phone?.number,
				ext: user.profile.phone?.extension,
				SMSEnabled: user.profile.phone?.isSMSEnabled,
				state: user.profile.state,
				zip: user.profile.zip
			} );
		}
	}, [
		setFormData,
		user
	] );

	const errors = useMemo( () => ( {
		userName: [
			{
				error: !formData.userName,
				message: 'Username is a required field.',
				fieldLabel: 'Username'
			}
		],
		firstName: [
			{
				error: !formData.firstName,
				message: 'First Name is a required field.',
				fieldLabel: 'First Name'
			}
		],
		lastName: [
			{
				error: !formData.lastName,
				message: 'Last Name is a required field.',
				fieldLabel: 'Last Name'
			}
		],
		zip: [
			{
				error: Boolean( formData.zip ) && !validateZip( formData.zip ),
				message: 'Please enter a valid, 5-digit zip code.',
				fieldLabel: 'Zip'
			}
		],
		email: [
			{
				error: !formData.email,
				message: 'Email is a required field.',
				fieldLabel: 'Email'
			},
			{
				error: Boolean( formData.email ) && !validateEmail( formData.email ),
				message: <>Please enter a valid email address.&nbsp;<EmailValidationInfoPopup /></>,
				fieldLabel: 'Email'
			}
		],
		phone: [
			{
				error: !formData.phone,
				message: 'Phone is a required field.',
				fieldLabel: 'Phone'
			},
			{
				error: Boolean( formData.phone ) && !validatePhone( formData.phone ),
				message: <>Please enter a valid U.S. phone number.&nbsp;<PhoneValidationInfoPopup /></>,
				fieldLabel: 'Phone'
			}
		]
	} ), [
		formData.email,
		formData.firstName,
		formData.lastName,
		formData.phone,
		formData.userName,
		formData.zip
	] );

	const validate = useCallback( () => Object.values( errors ).some( errs => errs.some( err => err.error ) ), [ errors ] );

	const mapFormErrors = useCallback(
		() => Object.values( errors ).map( errors => errors.map( error => error.error
			? <Message.Item key={ error.fieldLabel }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ errors ]
	);

	const handleEdit = useCallback( () => setEditable( true ), [] );

	const handleSave = useCallback( async () => {
		if ( validate() ) {
			if ( !forceValidation ) {
				setForceValidation( true );
			}
		} else {
			setForceValidation( false );

			let {
				address,
				address2,
				city,
				email,
				ext,
				firstName,
				lastName,
				emailNotifications,
				smsNotifications,
				notificationDigestType,
				phone,
				SMSEnabled,
				state,
				zip,
				...rest
			} = formData;

			const data = {
				...Object.fromEntries( Object.entries( rest ).filter( entry => !entry[ 0 ].startsWith( 'event-' ) ) ),
				notificationSettings: {
					digestType: { id: notificationDigestType },
					email: emailNotifications,
					events: Object.entries( rest )
						.reduce( ( acc, [ k, v ] ) => {
							if ( k.startsWith( 'event-' ) ) {
								const id = k.slice( 'event-'.length );

								return [ ...acc, { id, active: v } ];
							}

							return acc;
						}, [] ),
					sms: smsNotifications
				},
				profile: {
					address,
					address2,
					city,
					email,
					firstName,
					lastName,
					phone: {
						number: phone,
						extension: ext,
						isSMSEnabled: SMSEnabled
					},
					state,
					zip
				}
			};

			try {
				const response = await updateUser( data );

				setUser( response );
			} catch ( error ) {
				setApiErrorAlert( error );
			}
		}
	}, [
		forceValidation,
		formData,
		setApiErrorAlert,
		setUser,
		validate
	] );

	const handleCancel = useCallback( () => {
		setForceValidation( false );
		resetFormData();
		setEditable( false );
	}, [ resetFormData ] );

	const emailIcon = useMemo(
		() => !errors.email.some( error => error.error )
			? <Icon
				color="black"
				link
				name="mail"
				onClick={ () => console.log( 'email agency' ) }
			/>
			: null
		, [ errors.email ]
	);

	const countryCode = useMemo( () => <Label basic><span>+1&nbsp;</span></Label>, [] );

	if ( !user ) {
		return null;
	}

	return (
		<>
			<div className={ styles.container } >
				<header className={ styles.pageHeader }>
					<h2>
						Profile
						<HelpModal
							id={ 5 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</h2>
					<PermissionsWrapper permission={ permissions.agencyEditAgency.isAvailable }>
						{ editable
							? <div>
								<Button
									icon="save"
									inverted
									onClick={ handleSave }
								/>
								<Button
									icon="times"
									inverted
									onClick={ handleCancel }
								/>
							</div>
							: <Button
								disabled={ !permissions.agencyEditAgency.canExecute }
								icon="pencil"
								inverted
								onClick={ handleEdit }
								size="tiny"
							/>
						}
					</PermissionsWrapper>
				</header>
				<Segment
					basic
					as="div"
					className={ styles.content }
				>
					<div className={ styles.formContainer }>
						<Form autoComplete="off" error={ forceValidation && validate() }>
							<input
								className="hidden-input"
								name="city"
							/>

							<Form.Group>
								<FormInput
									errors={ errors.firstName }
									fieldLabel="First Name"
									forceValidation={ forceValidation }
									maxLength={ 255 }
									name="firstName"
									onChange={ updateField }
									readOnly={ !editable }
									required
									value={ formData.firstName || '' }
									width={ 6 }
								/>
								<FormInput
									errors={ errors.lastName }
									fieldLabel="Last Name"
									forceValidation={ forceValidation }
									maxLength={ 255 }
									name="lastName"
									onChange={ updateField }
									readOnly={ !editable }
									required
									value={ formData.lastName || '' }
									width={ 6 }
								/>
							</Form.Group>

							<Form.Group>
								<FormInput
									errors={ errors.userName }
									fieldLabel="Username"
									forceValidation={ forceValidation }
									maxLength={ 255 }
									name="userName"
									onChange={ updateField }
									readOnly={ !editable }
									required
									value={ formData.userName || '' }
									width={ 6 }
								/>
							</Form.Group>

							<Divider
								hidden
								style={ { margin: '1.5rem 0' } }
							/>

							<Form.Group>
								<FormInput
									fieldLabel="Address"
									name="address"
									onChange={ updateField }
									readOnly={ !editable }
									value={ formData.address || '' }
									width={ 10 }
								/>
							</Form.Group>

							<Form.Group>
								<FormInput
									fieldLabel="Address Line 2"
									name="address2"
									onChange={ updateField }
									readOnly={ !editable }
									value={ formData.address2 || '' }
									width={ 10 }
								/>
							</Form.Group>

							<Form.Group>
								<FormInput
									fieldLabel="City"
									name="city"
									onChange={ updateField }
									readOnly={ !editable }
									value={ formData.city || '' }
									width={ 6 }
								/>
								<FormSelect
									fieldLabel="State"
									maxLength={ 2 }
									name="state"
									onChange={ updateField }
									options={ stateOptions }
									readOnly={ !editable }
									search
									value={ formData.state || '' }
									width={ 2 }
								/>
								<FormInput
									errors={ errors.zip }
									fieldLabel="Zip"
									forceValidation={ forceValidation }
									keyDown="integer"
									maxLength={ 5 }
									name="zip"
									onChange={ updateField }
									readOnly={ !editable }
									value={ formData.zip || '' }
									width={ 2 }
								/>
							</Form.Group>

							<Divider
								hidden
								style={ { margin: '1.5rem 0' } }
							/>

							<Form.Group>
								<FormInput
									errors={ errors.email }
									fieldLabel="Email"
									forceValidation={ forceValidation }
									icon={ emailIcon }
									maxLength={ 254 }
									name="email"
									readOnly={ !editable }
									required
									value={ formData.email || '' }
									onChange={ updateField }
									width={ 6 }
								/>
							</Form.Group>

							<Form.Group>
								<FormInput
									errors={ errors.phone }
									fieldLabel="Phone"
									forceValidation={ forceValidation }
									keyDown="integer"
									inputLabel={ countryCode }
									mask="phone"
									maxLength={ 14 }
									name="phone"
									onChange={ updateField }
									readOnly={ !editable }
									required
									value={ formData.phone || '' }
									width={ 6 }
								/>
								<FormInput
									fieldLabel="Ext."
									forceValidation={ forceValidation }
									keyDown="integer"
									name="ext"
									onChange={ updateField }
									readOnly={ !editable }
									value={ formData.ext || '' }
									width={ 2 }
								/>
								<FormCheckbox
									checked={ formData.SMSEnabled }
									fieldLabel="SMS Enabled"
									name="SMSEnabled"
									onChange={ updateField }
									readOnly={ !editable }
									width={ 3 }
								/>
							</Form.Group>

							<Message
								error
								size="small"
							>
								<Message.Header>
                  There are errors in your submission. Please correct the following fields.
								</Message.Header>
								<br />
								<Message.List>
									{ mapFormErrors() }
								</Message.List>
							</Message>

						</Form>
					</div>
				</Segment>
			</div>
			<Divider hidden />
		</>
	);
};

export default Admin;
